import { useLanguage } from '@/i18n/language-hooks';
import { cn } from '@/lib/utils';
import { Monitor, Moon, Sun } from 'lucide-react';
import { useId } from 'react';

export type ThemeValue = 'light' | 'dark' | 'system';

const THEME_OPTIONS = [
  ['dark', Moon],
  ['light', Sun],
  ['system', Monitor],
] as const;

export interface ThemePickerProps {
  value: string | null | undefined;
  onChange: (value: ThemeValue) => void;
  disabled?: boolean;
  showTitle?: boolean;
  showLabels?: boolean;
  title?: string;
  groupLabel?: string;
  variant?: 'popover' | 'settings';
  className?: string;
  preventFocusSteal?: boolean;
}

export function ThemePicker({
  value,
  onChange,
  disabled = false,
  showTitle = false,
  showLabels = false,
  title,
  groupLabel,
  variant = 'popover',
  className,
  preventFocusSteal = false,
}: ThemePickerProps) {
  const { t } = useLanguage();
  const titleId = useId();

  const resolvedTitle = title ?? t('settings.preferences.display.theme.label');
  const resolvedGroupLabel = groupLabel ?? t('userMenu.theme');

  const groupClassName =
    variant === 'settings' ? 'border-fin-gray-700 bg-fin-gray-900/50' : 'border-border bg-muted/50';

  const selectedClassName =
    variant === 'settings'
      ? 'bg-fin-gray-800 text-fin-white shadow-sm'
      : 'bg-background text-foreground shadow-sm';

  const unselectedClassName =
    variant === 'settings'
      ? 'text-fin-gray-500 hover:bg-fin-gray-800/50 hover:text-fin-gray-300'
      : 'text-muted-foreground hover:bg-muted hover:text-foreground';

  return (
    <div className={className}>
      {showTitle ? (
        <span
          className={cn(
            'block text-xs',
            variant === 'settings' ? 'text-fin-gray-500' : 'text-muted-foreground'
          )}
          id={titleId}
        >
          {resolvedTitle}
        </span>
      ) : null}
      <div
        className={cn('flex w-full rounded-md border p-1', showTitle && 'mt-1.5', groupClassName)}
        role='radiogroup'
        aria-labelledby={showTitle ? titleId : undefined}
        aria-label={showTitle ? undefined : resolvedGroupLabel}
      >
        {THEME_OPTIONS.map(([optionValue, Icon]) => {
          const label = t(`userMenu.themeOptions.${optionValue}`);
          const selected = value === optionValue;

          return (
            <button
              key={optionValue}
              type='button'
              role='radio'
              aria-checked={selected}
              aria-label={label}
              disabled={disabled}
              onPointerDown={preventFocusSteal ? (event) => event.preventDefault() : undefined}
              onClick={() => onChange(optionValue)}
              className={cn(
                'flex min-w-0 flex-1 items-center justify-center rounded-sm transition-colors',
                showLabels ? 'gap-1.5 px-2 py-2 text-xs font-medium' : 'p-2',
                selected ? selectedClassName : unselectedClassName
              )}
            >
              <Icon
                className={cn('shrink-0', showLabels ? 'size-3.5 opacity-90' : 'size-4')}
                aria-hidden
              />
              {showLabels ? <span className='truncate'>{label}</span> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
